import * as T from 'three';
import {ledgeDuration} from './ledgeClimb';

type Sole={bone:T.Bone;bind:T.Quaternion;vertices:{mesh:T.SkinnedMesh;index:number}[]};
const RELEASE_TIME=.16;
/** The UAL clips supply the posture. Hands catch the lip, then the leading boot
 * stays on the top while the hips cross it. Contacts use the actual worn soles. */
export class LedgePose {
  private readonly target=new T.Vector3();
  private readonly shoulder=new T.Vector3();
  private readonly elbow=new T.Vector3();
  private readonly wrist=new T.Vector3();
  private readonly axis=new T.Vector3();
  private readonly pole=new T.Vector3();
  private readonly desired=new T.Vector3();
  private readonly from=new T.Vector3();
  private readonly to=new T.Vector3();
  private readonly q=new T.Quaternion();
  private readonly parent=new T.Quaternion();
  private readonly current=new T.Quaternion();
  private readonly footRotation=new T.Quaternion();
  private readonly groupRotation=new T.Quaternion();
  private readonly inverse=new T.Matrix4();
  private readonly sample=new T.Vector3();
  private readonly ankle=new T.Vector3();
  private readonly soles:(Sole|null)[]=[];
  private skeleton:T.Skeleton|null=null;
  private readonly footTargets=[new T.Vector3(),new T.Vector3()];
  private readonly footRotations=[new T.Quaternion(),new T.Quaternion()];
  private readonly correction=new T.Vector3();
  private readonly lastAnchor=new T.Vector3();
  private readonly releaseAnchor=new T.Vector3();
  readonly plantWeights=[0,0];
  private releaseAge=1;
  private wasClimbing=false;

  /** A teleport ends contacts immediately; it must never play the old lip's
   * grounded release at the destination. Keep the bound sole samples reusable. */
  reset():void {
    this.releaseAge=1;this.wasClimbing=false;this.plantWeights.fill(0);
    this.lastAnchor.set(0,0,0);this.releaseAnchor.set(0,0,0);
  }

  bind(skeleton:T.Skeleton,meshes:readonly T.SkinnedMesh[]):void {
    this.skeleton=skeleton;this.soles.length=0;this.reset();
    for(const side of ['l','r']){
      const index=skeleton.bones.findIndex(b=>b.name===`foot_${side}`),toe=skeleton.bones.findIndex(b=>b.name===`ball_${side}`);
      if(index<0){this.soles.push(null);continue;}
      const sole:Sole={bone:skeleton.bones[index],bind:new T.Quaternion().setFromRotationMatrix(this.inverse.copy(skeleton.boneInverses[index]).invert()),vertices:[]};
      for(const mesh of meshes){
        if(!mesh.visible)continue;
        const skin=mesh.geometry.attributes.skinIndex,weights=mesh.geometry.attributes.skinWeight;
        if(!skin||!weights)continue;
        for(let v=0;v<skin.count;v++){
          let weight=0;for(let k=0;k<4;k++)if(skin.getComponent(v,k)===index||skin.getComponent(v,k)===toe)weight+=weights.getComponent(v,k);
          if(weight>.5)sole.vertices.push({mesh,index:v});
        }
      }
      this.soles.push(sole);
    }
  }

  apply(group:T.Object3D,bones:Map<string,T.Bone>,state:number,age:number,anchor:T.Vector3,dt:number,grounded:boolean):void {
    if(state){this.lastAnchor.copy(anchor);group.localToWorld(this.lastAnchor);this.wasClimbing=true;}
    else {
      if(!grounded){this.releaseAge=1;this.wasClimbing=false;this.plantWeights.fill(0);return;}
      if(this.wasClimbing){this.releaseAge=0;this.wasClimbing=false;}
      this.releaseAge+=Math.max(0,dt);
      if(this.releaseAge>=RELEASE_TIME){this.plantWeights.fill(0);return;}
      // Keep the last top contact through the short handoff into grounded gait.
      // Store in world space, so forward movement cannot drag the planted boot.
      this.releaseAnchor.copy(this.lastAnchor);group.worldToLocal(this.releaseAnchor);anchor=this.releaseAnchor;
    }
    const t=state?age/ledgeDuration(state):1;
    group.updateWorldMatrix(true,true);
    group.getWorldQuaternion(this.groupRotation);this.inverse.copy(group.matrixWorld).invert();
    this.correction.set(0,0,0);let totalWeight=0;
    for(let i=0;i<2;i++){
      const sole=this.soles[i];this.plantWeights[i]=0;if(!sole)continue;
      const begin=i===1?(state===1?.18:.56):(state===1?.77:.82);
      const planted=i===1?(state===1?.30:.70):.96;
      const contact=state?T.MathUtils.smootherstep(t,begin,planted)*(i===1?1-T.MathUtils.smootherstep(t,.83,1):1)
        :i===0?1-T.MathUtils.smootherstep(this.releaseAge,0,RELEASE_TIME):0;
      if(contact<=0)continue;
      sole.bone.getWorldQuaternion(this.current);
      this.footRotation.copy(this.groupRotation).multiply(sole.bind);
      this.footRotation.slerpQuaternions(this.current,this.footRotation,contact);
      this.footRotations[i].copy(this.footRotation);
      sole.bone.parent!.getWorldQuaternion(this.parent).invert();
      sole.bone.quaternion.copy(this.parent).multiply(this.footRotation);
      // SkinnedMesh refreshes bindMatrixInverse in updateMatrixWorld (not
      // updateWorldMatrix). CPU sole samples must use this frame's root transform.
      group.updateMatrixWorld(true);this.skeleton?.update();
      sole.bone.getWorldPosition(this.ankle).applyMatrix4(this.inverse);
      let bottom=Infinity;
      for(const {mesh,index}of sole.vertices){mesh.getVertexPosition(index,this.sample).applyMatrix4(mesh.matrixWorld).applyMatrix4(this.inverse);bottom=Math.min(bottom,this.sample.y);}
      if(!Number.isFinite(bottom))continue;
      const target=this.footTargets[i];target.copy(anchor);target.x+=i===0?-.18:.18;target.z-=i===0?.72:.19;
      target.y+=this.ankle.y-bottom;group.localToWorld(target);
      sole.bone.getWorldPosition(this.ankle);
      this.correction.addScaledVector(this.sample.copy(target).sub(this.ankle),contact);totalWeight+=contact;
      this.plantWeights[i]=contact;
    }
    // The authored hip rise and the controller's climb path cannot both lift a
    // planted foot. Warp the pelvis through the contact, preserving the clip's
    // bent knee instead of forcing an overextended leg down to an unreachable top.
    const pelvis=bones.get('pelvis');
    if(pelvis&&totalWeight>0){
      this.correction.divideScalar(Math.max(1,totalWeight));
      pelvis.getWorldPosition(this.sample).add(this.correction);pelvis.parent!.worldToLocal(this.sample);
      pelvis.position.copy(this.sample);pelvis.updateWorldMatrix(false,true);
    }
    for(let i=0;i<2;i++){
      const sole=this.soles[i],contact=this.plantWeights[i];if(!sole||contact<=0)continue;
      const side=i===0?'l':'r';
      this.reach(bones,`thigh_${side}`,`calf_${side}`,`foot_${side}`,this.footTargets[i],contact);
      sole.bone.parent!.getWorldQuaternion(this.parent).invert();
      sole.bone.quaternion.copy(this.parent).multiply(this.footRotations[i]);
      sole.bone.updateWorldMatrix(false,true);
    }
    if(!state)return;
    const contact=T.MathUtils.smoothstep(t,.05,.16)*(1-T.MathUtils.smoothstep(t,state===1?.38:.42,state===1?.53:.62));
    if(contact>0)for(const side of ['l','r']){
      if(state===1&&side==='r')continue;
      this.target.copy(anchor);this.target.x+=side==='l'?-.26:.26;this.target.y+=.07;this.target.z-=.06;
      group.localToWorld(this.target);
      this.reach(bones,`upperarm_${side}`,`lowerarm_${side}`,`hand_${side}`,this.target,contact);
    }
  }
  private reach(bones:Map<string,T.Bone>,upperName:string,lowerName:string,endName:string,target:T.Vector3,weight:number):void {
    const upper=bones.get(upperName),lower=bones.get(lowerName),end=bones.get(endName);
    if(!upper||!lower||!end)return;
    upper.getWorldPosition(this.shoulder);lower.getWorldPosition(this.elbow);end.getWorldPosition(this.wrist);
    target.lerpVectors(this.wrist,target,weight);
    const a=this.shoulder.distanceTo(this.elbow),b=this.elbow.distanceTo(this.wrist);
    this.axis.copy(target).sub(this.shoulder);
    const distance=T.MathUtils.clamp(this.axis.length(),Math.abs(a-b)+.01,(a+b)*.985);
    this.axis.normalize();target.copy(this.shoulder).addScaledVector(this.axis,distance);
    this.pole.copy(this.elbow).sub(this.shoulder);this.pole.addScaledVector(this.axis,-this.pole.dot(this.axis));
    if(this.pole.lengthSq()<1e-6){
      this.pole.set(0,0,-1).applyQuaternion(this.groupRotation);
      if(Math.abs(this.pole.dot(this.axis))>.9)this.pole.set(1,0,0).applyQuaternion(this.groupRotation);
      this.pole.addScaledVector(this.axis,-this.pole.dot(this.axis));
    }
    this.pole.normalize();
    const along=(a*a-b*b+distance*distance)/(2*distance),height=Math.sqrt(Math.max(0,a*a-along*along));
    this.desired.copy(this.shoulder).addScaledVector(this.axis,along).addScaledVector(this.pole,height);
    this.rotate(upper,this.elbow,this.desired,this.shoulder);
    lower.getWorldPosition(this.elbow);end.getWorldPosition(this.wrist);
    this.rotate(lower,this.wrist,target,this.elbow);
  }
  private rotate(bone:T.Bone,from:T.Vector3,to:T.Vector3,pivot:T.Vector3):void {
    this.from.copy(from).sub(pivot).normalize();this.to.copy(to).sub(pivot).normalize();
    this.q.setFromUnitVectors(this.from,this.to);
    bone.getWorldQuaternion(this.current);bone.parent!.getWorldQuaternion(this.parent).invert();
    bone.quaternion.copy(this.parent).multiply(this.q).multiply(this.current);
    bone.updateWorldMatrix(false,true);
  }
}
